import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { addMixPanelEvent } from '../../store/actions';
import { trackGA4Event } from '../../utils/trackGA4Event';
import { ONBOARDING_STARTED, ONBOARDING_STEP_COMPLETED } from '../../constants/gtag-events';
import { onboardingStartedAtKey, onboardingUseCaseKey } from '../../utils/onboardingSession';
import Spinner from '../../components/Spinner';
import logo from "../../assets/logo-full.png";

const kindsOfWork = ["Content Creator", "Product Owner", "Educator", "Marketer", "Corporate Trainer", "Customer Support", "Podcaster", "Student", "Video Producer", "Writer / Author", "Digital Agency"];

const tagBaseStyle = {
    height: 50, border: '1px solid var(--bs-border-color)', borderRadius: 8,
    padding: 15, color: 'var(--bs-body-color)', lineHeight: '18px',
    cursor: 'pointer', transition: 'background 0.15s', userSelect: 'none',
    whiteSpace: 'nowrap',
};

function TagPill({ label, selected, onClick }) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            style={{
                ...tagBaseStyle,
                backgroundColor: selected
                    ? (hovered ? 'rgba(var(--bs-primary-rgb), 0.16)' : 'rgba(var(--bs-primary-rgb), 0.12)')
                    : (hovered ? 'rgba(var(--bs-primary-rgb), 0.04)' : 'transparent'),
                borderColor: selected ? 'var(--bs-primary)' : 'var(--bs-border-color)',
            }}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {label}
        </div>
    );
}

export default function OnboardingWork() {
    const [kindOfWork, setKindOfWork] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const dispatch = useDispatch();

    const urlParams = new URLSearchParams(window.location.search);
    const orgId = urlParams.get('orgId');
    const integrationId = urlParams.get('integrationId');

    // GROW-81: Onboarding - Started fires when the first onboarding question (this
    // screen) appears. Once per mount.
    useEffect(() => {
        if (!orgId) return;
        // don't re-fire if they come back to this step after going forward
        if (sessionStorage.getItem(onboardingStartedAtKey(orgId))) return;

        dispatch(addMixPanelEvent({
            orgId, body: {
                eventName: "ONBOARDING_STARTED",
                eventData: { total_steps: 2 },
            }
        }));
        // same event, GA4 side
        trackGA4Event(ONBOARDING_STARTED, { total_steps: 2 });
        sessionStorage.setItem(onboardingStartedAtKey(orgId), String(Date.now()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const updateKindOfWork = () => {
        if (!kindOfWork) return toast.error("Please select something from list");

        if (!orgId) {
            toast.error("Organization ID not found. Please try signing up again.");
            history.push('/');
            return;
        }

        setIsLoading(true);

        // Keep the tracked value to a fixed enum for clean Mixpanel segmentation —
        // free-text "Others" answers are still captured, just in a separate property.
        const isCustomAnswer = !kindsOfWork.includes(kindOfWork);

        dispatch(addMixPanelEvent({
            orgId: orgId, body: {
                eventName: "UPDATE_ORGANISATION_USE_CASE",
                eventData: {
                    useCase: isCustomAnswer ? 'Other' : kindOfWork,
                    ...(isCustomAnswer && { useCase_other_text: kindOfWork }),
                }
            }
        }));

        dispatch(addMixPanelEvent({
            orgId: orgId, body: {
                eventName: "ONBOARDING_STEP_COMPLETED",
                eventData: {
                    step_name: 'work_type',
                    step_number: 1,
                    value: isCustomAnswer ? 'Other' : kindOfWork,
                    ...(isCustomAnswer && { value_other_text: kindOfWork }),
                }
            }
        }));

        // GA4 step event
        trackGA4Event(ONBOARDING_STEP_COMPLETED, {
            step_number: 1,
            step_name: 'work_type',
            value: isCustomAnswer ? 'Other' : kindOfWork,
        });
        sessionStorage.setItem(onboardingUseCaseKey(orgId), isCustomAnswer ? 'Other' : kindOfWork);

        const audioUrl = integrationId
            ? `/onboarding/audio?orgId=${orgId}&integrationId=${integrationId}`
            : `/onboarding/audio?orgId=${orgId}`;

        history.push(audioUrl);
        setIsLoading(false);
    };

    return (
        <div className="container-fluid position-relative" style={{ minHeight: '100vh' }}>
            <div className="d-lg-none" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 4, background: 'var(--bs-primary)', zIndex: 10 }} />
            <div className="row" style={{ minHeight: "100vh" }}>
                <div className="col-12 d-flex flex-column justify-content-center align-items-center">
                    <div className="row mb-4">
                        <div className="col-12 text-center">
                            <img className="logo mb-4" src={logo} alt="logo" style={{ maxWidth: "200px" }} />
                        </div>
                    </div>

                    <div style={{ maxWidth: 520, width: '100%', padding: '0 16px' }}>
                        <div style={{ width: 150, height: 6, borderRadius: 10, background: 'var(--bs-border-color)', margin: '0 auto 20px' }}>
                            <div style={{ height: '100%', background: 'var(--bs-primary)', borderRadius: 10, transition: 'width 0.3s ease', width: '50%' }} />
                        </div>

                        <h4 className="fw-bold text-body-emphasis text-center mb-1">What kind of work do you do?</h4>
                        <p className="text-center mb-4" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>
                            This will help us personalize your experience in Listen2It.
                        </p>

                        <div className="onboarding-tags mb-3">
                            {kindsOfWork.map((tagName, tagNameIndex) => (
                                <div key={`kind-of-work-${tagNameIndex}`} className="onboarding-tag">
                                    <TagPill
                                        label={tagName}
                                        selected={tagName === kindOfWork}
                                        onClick={() => setKindOfWork(tagName)}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mb-3">
                            <label>Others</label>
                            <input
                                onChange={(e) => setKindOfWork(e.target.value)}
                                value={!kindsOfWork.includes(kindOfWork) ? kindOfWork : ""}
                                placeholder="Your answer"
                                type="text"
                                className="form-control"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={isLoading ? undefined : updateKindOfWork}
                            className="btn btn-primary w-100"
                            style={{ height: 44 }}
                        >
                            {isLoading ? <Spinner color="white" /> : 'Continue'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
